import { MOCK_CONVERSATIONS, MOCK_METRICS, type InboxMetrics } from "@/data/mock-conversations";
import { apiEvents } from "@/lib/api-events";
import type { Conversation, Session } from "@/types";

/**
 * Endpoints del portal (Cloudflare Worker → Apps Script Engine):
 *   POST /portal/auth/request-magic-link  → { ok }
 *   POST /portal/auth/verify-magic-link   → { ok, session_token, email, nombre, rol, expires_at }
 *   POST /portal/auth/validate-session    → { ok }   (401 si la sesión es inválida)
 *   POST /portal/conversations/list       → Conversation[]
 *   POST /portal/conversations/get        → Conversation
 *   POST /portal/metrics/inbox            → InboxMetrics
 *
 * Tenant id hardcoded a 'T001' por ahora; multi-tenant se resuelve más adelante
 * leyéndolo de env/config y/o decodificando del session_token.
 */
const API_URL = import.meta.env.VITE_API_URL ?? "https://api.aliadigital.co";
const TENANT_ID = import.meta.env.VITE_TENANT_ID ?? "T001";
const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === "true";

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

interface PortalRequestOptions {
  token?: string;
  /**
   * Si true, un 401 NO dispara el evento global "unauthorized".
   * Lo usa validateSession en la carga inicial para que ProtectedRoute
   * pueda hacer redirect silencioso sin toast.
   */
  suppressUnauthorizedEvent?: boolean;
}

async function portalRequest<T>(
  path: string,
  body: Record<string, unknown> = {},
  options: PortalRequestOptions = {}
): Promise<T> {
  const { token, suppressUnauthorizedEvent = false } = options;

  const response = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=utf-8" },
    body: JSON.stringify({
      source: "portal",
      tenant_id: TENANT_ID,
      ...(token ? { session_token: token } : {}),
      ...body,
    }),
  });

  if (response.status === 401 && !suppressUnauthorizedEvent) {
    apiEvents.dispatchEvent(new Event("unauthorized"));
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new ApiError(response.status, text || response.statusText);
  }

  return response.json() as Promise<T>;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/* -------------------------------------------------------------------------- */
/*  Auth                                                                      */
/* -------------------------------------------------------------------------- */

interface VerifyMagicLinkResponse {
  ok: boolean;
  session_token: string;
  email: string;
  nombre: string;
  rol: string;
  /** tenant del usuario; multi-tenant. Si el Engine no lo manda, cae a TENANT_ID. */
  tenant_id?: string;
  expires_at: string;
}

export const auth = {
  async requestMagicLink(email: string): Promise<{ ok: true }> {
    if (USE_MOCKS) {
      await wait(700);
      // eslint-disable-next-line no-console
      console.info("[auth-mock] magic link sent to", email);
      return { ok: true };
    }
    return portalRequest<{ ok: true }>(
      "/portal/auth/request-magic-link",
      { email }
    );
  },

  async verifyToken(token: string): Promise<Session> {
    if (USE_MOCKS) {
      await wait(400);
      return {
        token: token || "mock-session-token",
        email: "sebastian@aliadigital.co",
        name: "Sebastián",
        tenantId: TENANT_ID,
        rol: "super_admin",
      };
    }
    const res = await portalRequest<VerifyMagicLinkResponse>(
      "/portal/auth/verify-magic-link",
      { token }
    );
    return {
      token: res.session_token,
      email: res.email,
      name: res.nombre,
      tenantId: res.tenant_id ?? TENANT_ID,
      rol: res.rol,
    };
  },

  async validateSession(sessionToken: string): Promise<{ ok: true }> {
    if (USE_MOCKS) {
      await wait(80);
      return { ok: true };
    }
    return portalRequest<{ ok: true }>(
      "/portal/auth/validate-session",
      { session_token: sessionToken },
      { suppressUnauthorizedEvent: true }
    );
  },
};

/* -------------------------------------------------------------------------- */
/*  Messages                                                                  */
/* -------------------------------------------------------------------------- */

export interface SendMessageResponse {
  ok: true;
  lead_id: string;
  canal: string;
  message_id: string;
  timestamp: string;
  auto_take_over: boolean;
}

interface SendMessageErrorBody {
  error: string;
  detalle?: string;
  canal?: string;
  max?: number;
  actual?: number;
  [k: string]: unknown;
}

class SendMessageError extends Error {
  constructor(
    public code: string,
    public detail: SendMessageErrorBody
  ) {
    super(code);
    this.name = "SendMessageError";
  }
}

interface SendMessageArgs {
  leadId: string;
  type: "text";
  content: string;
  token?: string;
}

export const messages = {
  async send(args: SendMessageArgs): Promise<SendMessageResponse> {
    if (USE_MOCKS) {
      await wait(300);
      if (args.content === "MOCK_FAIL") {
        throw new SendMessageError("telegram_error", {
          error: "telegram_error",
          detalle: "Mock failure",
        });
      }
      return {
        ok: true,
        lead_id: args.leadId,
        canal: "whatsapp",
        message_id: `mock_${Date.now()}`,
        timestamp: new Date().toISOString(),
        auto_take_over: false,
      };
    }

    let res: SendMessageResponse | SendMessageErrorBody;
    try {
      res = await portalRequest<SendMessageResponse | SendMessageErrorBody>(
        "/portal",
        {
          action: "send_message",
          lead_id: args.leadId,
          type: args.type,
          content: args.content,
        },
        { token: args.token }
      );
    } catch (err) {
      if (err instanceof ApiError) {
        try {
          const parsed = JSON.parse(err.message) as SendMessageErrorBody;
          if (parsed.error) {
            throw new SendMessageError(parsed.error, parsed);
          }
        } catch (parseErr) {
          if (parseErr instanceof SendMessageError) throw parseErr;
        }
      }
      throw err;
    }

    if ("error" in res && typeof res.error === "string") {
      throw new SendMessageError(res.error, res);
    }

    return res as SendMessageResponse;
  },
};

/* -------------------------------------------------------------------------- */
/*  Handoff (take_over / release)                                             */
/* -------------------------------------------------------------------------- */

interface HandoffArgs {
  leadId: string;
  token?: string;
}

interface HandoffResponse {
  ok: true;
  [k: string]: unknown;
}

async function handoffRequest(
  action: "take_over" | "release",
  args: HandoffArgs
): Promise<HandoffResponse> {
  const res = await portalRequest<HandoffResponse | { error: string }>(
    "/portal",
    { action, lead_id: args.leadId },
    { token: args.token }
  );
  if ("error" in res && typeof res.error === "string") {
    throw new ApiError(400, res.error);
  }
  return res as HandoffResponse;
}

export const handoff = {
  async takeOver(args: HandoffArgs): Promise<HandoffResponse> {
    if (USE_MOCKS) {
      await wait(200);
      return { ok: true, lead_id: args.leadId };
    }
    return handoffRequest("take_over", args);
  },

  async release(args: HandoffArgs): Promise<HandoffResponse> {
    if (USE_MOCKS) {
      await wait(200);
      return { ok: true, lead_id: args.leadId };
    }
    return handoffRequest("release", args);
  },
};

/* -------------------------------------------------------------------------- */
/*  Conversations                                                             */
/* -------------------------------------------------------------------------- */

export const conversations = {
  async list(token?: string): Promise<Conversation[]> {
    if (USE_MOCKS) {
      await wait(250);
      return MOCK_CONVERSATIONS;
    }
    return portalRequest<Conversation[]>(
      "/portal/conversations/list",
      {},
      { token }
    );
  },

  async get(id: string, token?: string): Promise<Conversation | undefined> {
    if (USE_MOCKS) {
      await wait(120);
      return MOCK_CONVERSATIONS.find((c) => c.id === id);
    }
    try {
      return await portalRequest<Conversation>(
        "/portal/conversations/get",
        { conversation_id: id },
        { token }
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return undefined;
      throw err;
    }
  },

  async metrics(token?: string): Promise<InboxMetrics> {
    if (USE_MOCKS) {
      await wait(150);
      return MOCK_METRICS;
    }
    return portalRequest<InboxMetrics>(
      "/portal/metrics/inbox",
      {},
      { token }
    );
  },
};

export { ApiError, SendMessageError, portalRequest };
